/**
 * Stable A/B variant assignment for the landing page.
 *
 * Override hierarchy:
 *   1. `?v=A|B|C` query param (always wins, also persists into the cookie)
 *   2. Existing `inkling_lp_variant` cookie
 *   3. Fresh random draw, written to the cookie
 *
 * Cookie lives 90 days so the same visitor sees the same variant if they come
 * back. The variant id is reported on magic-link request so conversions are
 * attributable in the API log.
 */

export type LandingVariant = "A" | "B" | "C";

const COOKIE_NAME = "inkling_lp_variant";
const COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 90;
const VARIANTS: LandingVariant[] = ["A", "B", "C"];

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const escaped = name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&");
  const match = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"));
  if (!match) return null;
  const raw = match[1] ?? "";
  return decodeURIComponent(raw);
}

function writeCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE_SEC}; Path=/; SameSite=Lax${secure}`;
}

function isVariant(v: string | null | undefined): v is LandingVariant {
  return v === "A" || v === "B" || v === "C";
}

function pickRandom(): LandingVariant {
  const idx = Math.floor(Math.random() * VARIANTS.length);
  return VARIANTS[idx] ?? "A";
}

export function getLandingVariant(): LandingVariant {
  if (typeof window === "undefined") return "A";

  const url = new URL(window.location.href);
  const override = url.searchParams.get("v")?.toUpperCase();
  if (isVariant(override)) {
    writeCookie(COOKIE_NAME, override);
    return override;
  }

  const fromCookie = readCookie(COOKIE_NAME);
  if (isVariant(fromCookie)) return fromCookie;

  const fresh = pickRandom();
  writeCookie(COOKIE_NAME, fresh);
  return fresh;
}
